import { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Users,
  Settings,
  UserPlus,
  Link,
  Plus,
  UserRound,
  Clock,
  Trash2,
  Copy,
} from "lucide-react";
import { useConfirm } from "@tabipla/components";
import {
  useGroup,
  useGroupMembers,
  useRemoveMember,
  useUpdateMemberRole,
} from "../../application/hooks/useGroups";
import { useDefaultSession } from "../../application/hooks/useDefaultSession";
import { useAuth } from "../../application/hooks/useAuth";
import { SessionDetail } from "../components/expenses/SessionDetail";
import { InviteLinkDialog } from "../components/InviteLinkDialog";
import { useGroupInvitations, useDeleteInvitation } from "../../application/hooks/useInvitations";
import { ExpenseForm } from "../components/expenses/ExpenseForm";
import { Button, Card, CardContent, CardHeader, CardTitle, Badge, LoadingState } from "@tabipla/ui";
import { WarittoLayout } from "../layouts/WarittoLayout";
import { ErrorState } from "../components/ErrorState";
import { useCreateExpense } from "../../application/hooks/useExpenses";
import type { GroupRole, MemberType, CreateExpenseDto, UpdateExpenseDto } from "@tabipla/shared";
import { MemberAvatarStack } from "../components/MemberAvatarStack";
import {
  InviteMemberDialog,
  GroupSettingsDialog,
  AddGuestDialog,
  MemberCard,
} from "../components/group-detail";

export function GroupDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { data: group, isLoading, error, refetch } = useGroup(id);
  const { data: members } = useGroupMembers(id);

  const removeMutation = useRemoveMember(id ?? "");
  const updateRoleMutation = useUpdateMemberRole(id ?? "");

  // Auth
  const { participant } = useAuth();

  // Default session (auto-created if needed)
  const isOwner = group?.myRole === "owner";
  const isOwnerOrEditor = isOwner || group?.myRole === "editor";

  // Invitations (only for owner)
  const { data: invitations } = useGroupInvitations(isOwner ? id : undefined);
  const deleteInvitationMutation = useDeleteInvitation(id ?? "");
  const { session: defaultSession, isLoading: sessionLoading } = useDefaultSession(
    id,
    isOwnerOrEditor
  );

  const { confirm, ConfirmDialog } = useConfirm();

  // Ref for scrolling to member section
  const memberSectionRef = useRef<HTMLDivElement>(null);

  // Dialog states
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showInviteLinkDialog, setShowInviteLinkDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showAddGuestDialog, setShowAddGuestDialog] = useState(false);

  // Create expense mutation
  const createExpenseMutation = useCreateExpense(id ?? "", defaultSession?.id ?? "");

  const handleRemoveMember = async (memberId: string, memberType: MemberType) => {
    const confirmed = await confirm({
      title: t("groups.removeMemberTitle", "メンバーを削除"),
      description: t("groups.removeMemberConfirm"),
      confirmText: t("common.delete", "削除"),
      variant: "destructive",
    });

    if (confirmed) {
      try {
        await removeMutation.mutateAsync({ memberId, memberType });
        toast.success(t("groups.removeMemberSuccess", "メンバーを削除しました"));
      } catch {
        toast.error(t("groups.removeMemberError", "削除に失敗しました"));
      }
    }
  };

  const handleUpdateRole = async (
    memberId: string,
    memberType: MemberType,
    role: Exclude<GroupRole, "owner">
  ) => {
    try {
      await updateRoleMutation.mutateAsync({ memberId, memberType, role });
      toast.success(t("groups.updateRoleSuccess", "権限を変更しました"));
    } catch {
      toast.error(t("groups.updateRoleError", "変更に失敗しました"));
    }
  };

  const handleCreateExpense = async (expense: CreateExpenseDto | UpdateExpenseDto) => {
    try {
      await createExpenseMutation.mutateAsync(expense as CreateExpenseDto);
      toast.success(t("expenses.createSuccess", "経費を追加しました"));
      setShowExpenseForm(false);
    } catch {
      toast.error(t("expenses.createError", "経費の追加に失敗しました"));
    }
  };

  const handleCopyInviteLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("invitations.linkCopied", "リンクをコピーしました"));
    } catch {
      toast.error(t("invitations.copyError", "コピーに失敗しました"));
    }
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    const confirmed = await confirm({
      title: t("invitations.deleteTitle", "招待リンクを削除"),
      description: t("invitations.deleteConfirm", "この招待リンクを削除しますか？"),
      confirmText: t("common.delete", "削除"),
      variant: "destructive",
    });

    if (confirmed) {
      try {
        await deleteInvitationMutation.mutateAsync(invitationId);
        toast.success(t("invitations.deleteSuccess", "招待リンクを削除しました"));
      } catch {
        toast.error(t("invitations.deleteError", "削除に失敗しました"));
      }
    }
  };

  const scrollToMembers = () => {
    memberSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  if (isLoading) {
    return (
      <WarittoLayout title={t("groups.detail")}>
        <LoadingState message={t("groups.loadingDetail")} />
      </WarittoLayout>
    );
  }

  if (error) {
    return (
      <WarittoLayout title={t("groups.detail")}>
        <ErrorState error={error} onRetry={refetch} />
      </WarittoLayout>
    );
  }

  if (!group) {
    return (
      <WarittoLayout title={t("groups.detail")}>
        <ErrorState error={new Error(t("groups.notFound"))} />
      </WarittoLayout>
    );
  }

  const canEdit = isOwnerOrEditor;

  // FAB actions
  const fabActions = [];
  if (canEdit && defaultSession) {
    fabActions.push({
      icon: Plus,
      label: t("expenses.addExpense", "出費を追加"),
      onClick: () => setShowExpenseForm(true),
    });
  }
  if (isOwner) {
    fabActions.push({
      icon: UserPlus,
      label: t("groups.invite", "メンバー招待"),
      onClick: () => setShowInviteLinkDialog(true),
    });
  }

  return (
    <WarittoLayout title={group.name} fabActions={fabActions.length > 0 ? fabActions : undefined}>
      <div className="animate-fade-in space-y-6">
        {/* Header */}
        <div className="hidden lg:block">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-4">
                <h1 className="text-2xl font-bold tracking-tight">{group.name}</h1>
                {members && members.length > 0 && (
                  <MemberAvatarStack members={members} onPress={scrollToMembers} />
                )}
              </div>
              {group.description && (
                <p className="mt-1 text-muted-foreground">{group.description}</p>
              )}
            </div>
            {isOwner && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowSettingsDialog(true)}>
                  <Settings className="mr-2 h-4 w-4" />
                  {t("groups.settings")}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowInviteLinkDialog(true)}>
                  <Link className="mr-2 h-4 w-4" />
                  {t("invitations.linkInvite")}
                </Button>
                <Button
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600"
                  onClick={() => setShowInviteDialog(true)}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  {t("groups.invite")}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Group Description */}
        {group.description && (
          <p className="text-sm text-muted-foreground lg:hidden">{group.description}</p>
        )}

        {/* Mobile Header Actions with Member Avatars */}
        <div className="flex items-center justify-between gap-2 lg:hidden">
          {members && members.length > 0 && (
            <MemberAvatarStack members={members} onPress={scrollToMembers} />
          )}
          {isOwner && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-shrink-0"
                onClick={() => setShowSettingsDialog(true)}
              >
                <Settings className="mr-2 h-4 w-4" />
                {t("groups.settings")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-shrink-0"
                onClick={() => setShowInviteLinkDialog(true)}
              >
                <Link className="mr-2 h-4 w-4" />
                {t("invitations.linkInvite")}
              </Button>
            </div>
          )}
        </div>

        {/* Session Detail - Main Content */}
        {sessionLoading ? (
          <LoadingState message={t("sessions.loading", "読み込み中...")} />
        ) : defaultSession ? (
          <SessionDetail
            groupId={id ?? ""}
            session={defaultSession}
            members={members ?? []}
            currentUserId={participant?.id ?? ""}
            currentUserType={participant?.type ?? "user"}
            canEdit={canEdit}
            simplified
          />
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              {t("sessions.noSessions", "精算がありません")}
            </CardContent>
          </Card>
        )}

        {/* Members Section */}
        <div ref={memberSectionRef} className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center text-base">
                  <Users className="mr-2 h-5 w-5" />
                  {t("groups.joinedMembers", "参加済み")} ({members?.length ?? 0})
                </CardTitle>
                {isOwner && (
                  <Button variant="outline" size="sm" onClick={() => setShowAddGuestDialog(true)}>
                    <UserRound className="mr-2 h-4 w-4" />
                    {t("groups.addGuest", "ゲスト追加")}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {members?.map((member) => (
                <MemberCard
                  key={`${member.memberType}:${member.memberId}`}
                  memberId={member.memberId}
                  memberType={member.memberType}
                  memberName={member.memberName}
                  memberEmail={member.memberEmail}
                  role={member.role}
                  isOwner={isOwner}
                  onUpdateRole={handleUpdateRole}
                  onRemove={handleRemoveMember}
                />
              ))}
            </CardContent>
          </Card>

          {/* Pending Invitations Section (Owner only) */}
          {isOwner && invitations && invitations.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center text-base">
                  <Clock className="mr-2 h-5 w-5 text-amber-500" />
                  {t("groups.pendingInvitations", "招待中")} ({invitations.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {invitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-800 dark:bg-amber-900/20"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Link className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                          <p className="truncate text-sm font-medium">
                            {t("invitations.inviteLink", "招待リンク")}
                          </p>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {t("invitations.usedCount", "使用回数")}: {invitation.usedCount}
                          {invitation.maxUses && ` / ${invitation.maxUses}`}
                          {invitation.expiresAt && (
                            <>
                              {" • "}
                              {t("invitations.expiresAt", "期限")}:{" "}
                              {new Date(invitation.expiresAt).toLocaleDateString()}
                            </>
                          )}
                        </p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                        {t(`groups.roles.${invitation.role}`)}
                      </Badge>
                    </div>
                    <div className="mt-2 flex items-center justify-end gap-2 border-t border-amber-200 pt-2 dark:border-amber-800">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => handleCopyInviteLink(invitation.url)}
                      >
                        <Copy className="mr-1 h-4 w-4" />
                        {t("invitations.copy", "コピー")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs text-destructive hover:bg-destructive/10"
                        onClick={() => handleDeleteInvitation(invitation.id)}
                      >
                        <Trash2 className="mr-1 h-4 w-4" />
                        {t("common.delete", "削除")}
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <InviteMemberDialog
        groupId={id ?? ""}
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
      />

      <GroupSettingsDialog
        groupId={id ?? ""}
        groupName={group.name}
        groupDescription={group.description}
        open={showSettingsDialog}
        onOpenChange={setShowSettingsDialog}
      />

      <AddGuestDialog
        groupId={id ?? ""}
        open={showAddGuestDialog}
        onOpenChange={setShowAddGuestDialog}
      />

      <InviteLinkDialog
        open={showInviteLinkDialog}
        onOpenChange={setShowInviteLinkDialog}
        groupId={id ?? ""}
        groupName={group?.name}
      />

      {/* Expense Form */}
      {canEdit && defaultSession && members && (
        <ExpenseForm
          open={showExpenseForm}
          onOpenChange={setShowExpenseForm}
          members={members}
          currentUserId={participant?.id ?? ""}
          sessionId={defaultSession.id}
          editingExpense={null}
          onSubmit={handleCreateExpense}
          isSubmitting={createExpenseMutation.isPending}
        />
      )}

      <ConfirmDialog />
    </WarittoLayout>
  );
}
